import { useScansExportStore } from "@/modes/export-mode/scans-export/scans-export-store";
import {
  useIsExportableDataset,
  usePrepareScansExportData,
} from "@/modes/export-mode/scans-export/use-prepare-scans-export-data";
import { setClippingBox } from "@/store/clipping-box-slice";
import { setIpxScanSelection } from "@/store/modes/ipx-order-mode-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectIElementWorldPosition } from "@faro-lotv/project-source";
import { isEqual } from "es-toolkit";
import { useEffect, useMemo, type JSX } from "react";
import { collectScanIds } from "./ipx-scan-selection";

/**
 * Reports the scans selected in the list to the wizard state.
 * Rendered by the scene, as the step panel is not mounted while it is collapsed on small screens.
 *
 * @returns the reporter, once the point cloud is a dataset made of scans
 */
export function IpxScanSelectionReporter(): JSX.Element | null {
  return useIsExportableDataset() ? <ScanSelectionReporter /> : null;
}

/** @returns nothing, the scans data needs a dataset made of scans */
function ScanSelectionReporter(): null {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { clusterTree, invalidClusterIds, scanToPanoPathMap } =
    usePrepareScansExportData();
  const selectedIds = useScansExportStore((state) => state.selectedIds);
  const allScanIds = useMemo(
    () => collectScanIds(clusterTree, invalidClusterIds),
    [clusterTree, invalidClusterIds],
  );
  const isInitialized = useAppSelector(
    (state) => state.ipxOrderMode.totalScanCount !== undefined,
  );

  useEffect(() => {
    if (!allScanIds.length) return;
    const state = store.getState();
    const previous = state.ipxOrderMode.selectedScanIds;
    if (isEqual(previous, selectedIds) && isInitialized) return;

    const sentScans = (selectedIds.length ? selectedIds : allScanIds).map(
      (id) => ({
        id,
        position: selectIElementWorldPosition(
          scanToPanoPathMap.forward.get(id) ?? id,
        )(state),
      }),
    );
    // The first box is placed around the initial selection, later the user's box is kept and Reset fits it to the selection
    if (!isInitialized) dispatch(setClippingBox(undefined));
    dispatch(
      setIpxScanSelection({
        totalScanCount: allScanIds.length,
        selectedScanIds: selectedIds,
        sentScans,
      }),
    );
  }, [
    allScanIds,
    dispatch,
    isInitialized,
    scanToPanoPathMap.forward,
    selectedIds,
    store,
  ]);
  return null;
}
